/**
 * Template inputs (platform-neutral, no JSX): the small form a research
 * template opens before it is enabled (a topic, a link to watch, or one of the
 * user's PDFs). Web and native forms share these rules; the server validates
 * again and answers with the field to fix.
 */
import type { NovaTemplateInputs, NovaTemplateInputSpec } from '@tabbio/types';
import {
  isNovaTemplateInputError,
  type NovaTemplateSummary,
} from '../../lib/automation-api';

export type NovaTemplateInputKey = NovaTemplateInputSpec['key'];

export type NovaTemplateInputValues = Record<NovaTemplateInputKey, string>;

export type NovaTemplateInputErrors = Partial<
  Record<NovaTemplateInputKey, string>
>;

/** Bounds that match the server's template input schema. */
export const NOVA_TEMPLATE_INPUT_LIMITS: Record<NovaTemplateInputKey, number> = {
  topic: 200,
  region: 80,
  url: 2048,
  asset_id: 64,
};

/** A template that must ask before it is enabled. */
export function templateNeedsInputs(template: NovaTemplateSummary): boolean {
  return Array.isArray(template.inputs) && template.inputs.length > 0;
}

export function emptyTemplateInputs(): NovaTemplateInputValues {
  return { topic: '', region: '', url: '', asset_id: '' };
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Client checks before the request: required fields, a real http(s) link and
 * a picked PDF. Returns i18n keys per field (empty when the form can submit).
 */
export function validateTemplateInputs(
  specs: readonly NovaTemplateInputSpec[],
  values: NovaTemplateInputValues,
): NovaTemplateInputErrors {
  const errors: NovaTemplateInputErrors = {};
  for (const spec of specs) {
    const value = values[spec.key]?.trim() ?? '';
    if (spec.kind === 'url') {
      if (!value || !isHttpUrl(value)) {
        errors.url = 'nova.templateInputs.urlInvalid';
      }
      continue;
    }
    if (spec.kind === 'pdf_asset') {
      if (!value) errors.asset_id = 'nova.templateInputs.pdfRequired';
      continue;
    }
    if (spec.required && !value) {
      errors[spec.key] = 'nova.templateInputs.required';
    }
  }
  return errors;
}

/** The inputs the enable request sends: trimmed, empty ones left out. */
export function templateInputsPayload(
  specs: readonly NovaTemplateInputSpec[],
  values: NovaTemplateInputValues,
): NovaTemplateInputs {
  const payload: NovaTemplateInputs = {};
  for (const spec of specs) {
    const value = values[spec.key]?.trim();
    if (value) {
      payload[spec.key] = value.slice(0, NOVA_TEMPLATE_INPUT_LIMITS[spec.key]);
    }
  }
  return payload;
}

/**
 * The server's answer to an unusable input: the field it names and its plain
 * message, shown under that field. Null for any other error.
 */
export function templateInputServerError(
  error: unknown,
): { field: NovaTemplateInputKey; message: string } | null {
  if (!isNovaTemplateInputError(error)) return null;
  const field = error.meta?.field;
  const key: NovaTemplateInputKey =
    field === 'topic' || field === 'region' || field === 'url' || field === 'asset_id'
      ? field
      : 'topic';
  return { field: key, message: error.message };
}

/** Props of the template inputs form (web dialog and native sheet alike). */
export interface NovaTemplateInputsFormProps {
  /** The template being set up; null when the form is closed. */
  template: NovaTemplateSummary | null;
  busy: boolean;
  /** The server named a field to fix (plain words for under that field). */
  serverError: { field: NovaTemplateInputKey; message: string } | null;
  onClose: () => void;
  onSubmit: (values: NovaTemplateInputValues) => void;
}
